import { defineStore } from 'pinia';
import Bugsnag from '@bugsnag/js';
import { useProjectStore } from './projectStore';
import type { PaymentType } from '@/types/Config';
import type { PaymentsState } from '@/types/Payments';
import notificationWrapper from '@/utils/helpers/notificationWrapper';
import { useConfigStore } from './configStore';
import { useOfferStore } from './offerStore';
import type { Offer } from '@/types/Offers';

export const usePaymentsStore = defineStore('payments', {
	state: (): PaymentsState => ({
		paymentTypes: [],
		paymentTypesConfig: {
			paymentTypesTitle: 'payment.paymentTypes.title',
			paymentTypes: {
				creditCard: {
					key: 'creditCard',
					display: true,
					options: {
						showOnlyDefault: true,
						hideCardForm: true,
						title: 'payment.authorization.title',
						content: 'payment.authorization.panelText',
					},
				},
				bankTransfer: {
					key: 'bankTransfer',
					display: true,
					options: {
						title: 'payment.wireTransfer.title',
						content: 'payment.wireTransfer.panelText',
					},
				},
			},
		},
		selectedPayment: '',
		bankTransferLoading: false,
	}),

	actions: {
		async createBankTransfer(offerId: string) {
			try {
				this.bankTransferLoading = true;
				const projectStore = useProjectStore();
				const configStore = useConfigStore();
				const offerStore = useOfferStore();
				const data = await window.appConnector.createBankTransfer(projectStore.projectIdSelected, offerId);
				if (data) {
					configStore.setOffer(data);
					offerStore.updateOffer({ ...offerStore.offer, ...data });
					offerStore.offer = { ...offerStore.offer, ...data };
				}
				this.bankTransferLoading = false;
			} catch (error) {
				this.bankTransferLoading = false;
				notificationWrapper('error', 'DEFAULT');
				throw error;
			}
		},
		async getPaymentTypes(offer: Offer) {
			const configStore = useConfigStore();
			try {
				const paymentTypes = Object.fromEntries(
					Object.entries((this.paymentTypesConfig.paymentTypes) ?? {}).filter((entry): entry is [string, PaymentType] => {
						const payment = entry[1];
						return payment != null && typeof (payment as PaymentType).key === 'string' && (payment as PaymentType).key.length > 0;
					}),
				);
				this.paymentTypes = [];
				if (Object.keys(paymentTypes).length === 0) return;
				const projectStore = useProjectStore();
				const data = await window.appConnector.getPaymentTypes(projectStore.projectIdSelected, offer.id, {
					payment_types: Object.values(paymentTypes).map((value) => value.key),
				});
				for (const refPayment of Object.keys(paymentTypes)) {
					const isIncluded = !!data.payment_types.includes(paymentTypes[refPayment].key);
					paymentTypes[refPayment].display = isIncluded;
					if (isIncluded) {
						this.paymentTypes.push(paymentTypes[refPayment].key);
					}
				}
				if (configStore.keyConfig && this.paymentTypes.length === 0) {
					notificationWrapper('error', 'NO_PAYMENT_TYPES');
					Bugsnag.notify(new Error(`No payment types found for config ${configStore.keyConfig} - ${configStore.isPrebooking ? 'prebook' : 'book'}`));
				}
			} catch (error) {
				throw error;
			}
		},
	},
});
